import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { wabaApi } from "../../lib/wabaApi";
import { SOURCE_META } from "./MetricsCharts";
import { DialogPanel } from "../shared/DialogPanel";
import { backdropClose } from "../shared/backdrop";
import { ExecModal, execMeta, type ExecRow } from "../shared/ExecModal";

const WabaMetricsChart = lazy(() => import("./MetricsCharts"));

interface FilterConn {
  id: string;
  name: string;
  agent_id: string | null;
  agent_name: string | null;
  active: boolean;
}

interface Overview {
  messages: {
    total: number; inbound: number; outbound: number;
    by_source: { ia: number; crm: number; ia_externa: number; api: number; aparelho: number };
  };
  send: { api_total: number; failed: number };
  delivery: { sent: number; delivered: number; read: number; failed: number };
  leads: { new: number; active: number; ctwa: number; curious: number; engaged: number; curious_ia: number };
  ia_per_lead: { responses: number; balloons: number; leads: number };
  handoff: { pairs: number; contacts: number; ia_contacts: number; median_seconds: number | null; avg_seconds: number | null };
  calls: { total: number; answered: number; avg_duration_seconds: number | null };
  daily: { date: string; inbound: number; outbound: number; ia: number; crm: number; ia_externa: number; api: number; aparelho: number }[];
  ia_funnel: { bucket: string; leads: number }[];
  top_connections: { connection_id: string; name: string; inbound: number; outbound: number }[];
}

const PERIODS = [
  { value: 1, label: "Hoje" },
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
];

function fmtNumber(n: number) {
  return n.toLocaleString("pt-BR");
}

function fmtPct(num: number, den: number) {
  if (!den) return "—";
  return `${((num / den) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

function fmtDuration(s: number | null | undefined) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  const h = Math.floor(s / 3600);
  return `${h}h ${Math.round((s % 3600) / 60)}min`;
}

// Dia em Brasília → limites UTC (created_at do log é UTC naive; BRT = UTC-3 fixo).
export function brtDayStartUtc(day: string) {
  return new Date(`${day}T00:00:00-03:00`).toISOString();
}

export function todayBrt() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
}

export function shiftDay(day: string, delta: number) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

interface Failure {
  id: string;
  created_at: string;
  to_number: string | null;
  event_type: string;
  source: string | null;
  send_error: string | null;
  connection_id: string;
  connection_name: string;
  agent_id: string | null;
  agent_name: string | null;
  execution: (ExecRow & { agent_name: string | null }) | null;
}

// created_at do log é UTC naive — acrescenta Z antes de formatar em Brasília.
export function fmtLogTime(iso: string) {
  return new Date(iso.endsWith("Z") ? iso : `${iso}Z`).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

interface Card {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  tooltip?: string;
  onClick?: () => void;
  ia?: boolean;  // true → seção "Inteligência artificial"; ausente → geral
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "18px 20px" }}>
      <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ChartFallback() {
  return <div style={{ padding: 24, fontSize: 12, color: "var(--ink-dim)" }}>Carregando gráfico…</div>;
}

function EmptyChart() {
  return <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "16px 0", textAlign: "center" }}>Sem dados no período</div>;
}

function SectionHeading({ children, top }: { children: React.ReactNode; top?: boolean }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--ink-secondary)", margin: top ? "28px 0 12px" : "0 0 12px" }}>
      {children}
    </div>
  );
}

function CardGrid({ list }: { list: Card[] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
      {list.map(card => (
        <div
          key={card.label}
          className="metric-card"
          title={card.tooltip}
          onClick={card.onClick}
          style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "18px 20px", cursor: card.onClick ? "pointer" : card.tooltip ? "help" : undefined }}
        >
          <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>
            {card.label}
          </div>
          <div className="metric-value" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: card.color }}>
            {card.value}
          </div>
          {card.sub && (
            <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>
              {card.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Select com busca interna. Fecha ao clicar fora (overlay) ou Esc.
export function SearchSelect({ value, onChange, options, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; dot?: string }[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const current = options.find(o => o.value === value);
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q.toLowerCase())) : options;

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => { setOpen(v => !v); setQ(""); }}
        style={{
          display: "flex", alignItems: "center", gap: 8, minWidth: 200, maxWidth: 260,
          padding: "6px 10px", background: "var(--surface-hover)",
          border: `1px solid ${value ? "var(--brand)" : "var(--line)"}`, borderRadius: 8,
          color: value ? "var(--ink)" : "var(--ink-muted)", fontSize: 12, cursor: "pointer",
        }}
      >
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {current?.label ?? placeholder}
        </span>
        <span style={{ fontSize: 9, color: "var(--ink-muted)" }}>▼</span>
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 998 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 999, width: 260,
            background: "var(--surface-card)", border: "1px solid var(--line-bold)",
            borderRadius: 8, boxShadow: "0 8px 24px var(--overlay)", overflow: "hidden",
          }}>
            <input
              autoFocus
              placeholder="Buscar pelo nome..."
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Escape" && setOpen(false)}
              style={{
                width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "transparent",
                border: "none", borderBottom: "1px solid var(--line)", color: "var(--ink)",
                fontSize: 12.5, outline: "none",
              }}
            />
            <div style={{ maxHeight: 260, overflowY: "auto", padding: 4 }}>
              {[{ value: "", label: placeholder }, ...filtered].map(o => (
                <div
                  key={o.value || "__all"}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  style={{
                    padding: "7px 10px", borderRadius: 6, fontSize: 12.5, cursor: "pointer",
                    color: o.value === value ? "var(--brand)" : "var(--ink)",
                    background: o.value === value ? "rgba(195,32,32,0.08)" : "transparent",
                  }}
                  onMouseOver={e => { if (o.value !== value) e.currentTarget.style.background = "var(--surface-hover)"; }}
                  onMouseOut={e => { if (o.value !== value) e.currentTarget.style.background = "transparent"; }}
                >
                  {"dot" in o && o.dot && (
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 99, background: o.dot, marginRight: 7, flexShrink: 0 }} />
                  )}
                  {o.label}
                </div>
              ))}
              {filtered.length === 0 && q && (
                <div style={{ padding: "10px", fontSize: 12, color: "var(--ink-muted)" }}>Nenhum resultado.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function WabaMetricsTab({ canSeeAll }: { canSeeAll: boolean }) {
  const [period, setPeriod] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [connectionId, setConnectionId] = useState("");
  // Com a permissão waba_metricas_todas (ou Admin), abre em "Todas"; o toggle
  // restringe pra dono/participante. Sem a permissão o backend já força "mine".
  const [scopeMine, setScopeMine] = useState(false);
  const rangeActive = !!(startDate && endDate);

  const today = todayBrt();
  const dateFrom = rangeActive ? brtDayStartUtc(startDate) : brtDayStartUtc(shiftDay(today, -(period - 1)));
  const dateTo = rangeActive ? brtDayStartUtc(shiftDay(endDate, 1)) : brtDayStartUtc(shiftDay(today, 1));

  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  if (connectionId) params.set("connection_id", connectionId);
  if (scopeMine) params.set("scope", "mine");

  const { data, isLoading, error } = useQuery<Overview>({
    queryKey: ["waba-metrics", dateFrom, dateTo, connectionId, scopeMine],
    queryFn: () => wabaApi.get(`/metrics/overview?${params.toString()}`),
  });

  const [showFailures, setShowFailures] = useState(false);
  const [execSel, setExecSel] = useState<{ exec: ExecRow; stepsUrl: string; subtitle?: string } | null>(null);
  const { data: failData, isLoading: failLoading } = useQuery<{ failures: Failure[] }>({
    queryKey: ["waba-failures", dateFrom, dateTo, connectionId, scopeMine],
    queryFn: () => wabaApi.get(`/metrics/failures?${params.toString()}`),
    enabled: showFailures,
  });

  // Opções do filtro vêm de um endpoint leve próprio (carrega na hora, sem
  // esperar o overview pesado) e respeitam o toggle Todas/Minhas. Sem select
  // de agente: agente ↔ conexão é 1:1, o nome dele entra no label da conexão.
  const { data: connList } = useQuery<FilterConn[]>({
    queryKey: ["waba-metrics-conns", scopeMine],
    queryFn: () => wabaApi.get(`/metrics/connections${scopeMine ? "?scope=mine" : ""}`),
  });
  const connOptions = (connList ?? []).map(c => ({
    value: c.id,
    label: c.agent_name ? `${c.name} — ${c.agent_name}` : c.name,
    dot: c.active ? "var(--ok)" : "var(--danger)",
  }));

  const cards: Card[] = [
    {
      label: "Mensagens processadas",
      value: data ? fmtNumber(data.messages.total) : "—",
      sub: data ? `${fmtNumber(data.messages.inbound)} recebidas · ${fmtNumber(data.messages.outbound)} enviadas` : undefined,
    },
    {
      label: "Respostas da IA",
      ia: true,
      value: data ? fmtNumber(data.ia_per_lead.responses) : "—",
      sub: data ? `${fmtNumber(data.ia_per_lead.balloons)} balões` : undefined,
      tooltip: "Balões consecutivos ao mesmo lead com até 15s de intervalo (picotagem do motor) contam como UMA resposta. Em conexões onde a IA do Valquíria não atua no período (IA externa, ex.: n8n), envios via API sem source declarado também contam como IA.",
    },
    {
      label: "Respostas da IA por lead",
      ia: true,
      value: data && data.ia_per_lead.leads > 0
        ? (data.ia_per_lead.responses / data.ia_per_lead.leads).toLocaleString("pt-BR", { maximumFractionDigits: 1 })
        : "—",
      sub: data ? `${fmtNumber(data.ia_per_lead.responses)} respostas · ${fmtNumber(data.ia_per_lead.leads)} leads` : undefined,
      tooltip: "Respostas da IA (picotagem agrupada: balões com até 15s de gap = 1 resposta) dividido pelos leads distintos que as receberam.",
    },
    {
      label: "Tempo até humano",
      ia: true,
      value: data ? fmtDuration(data.handoff.avg_seconds) : "—",
      tooltip: "Média da distância entre a última mensagem da IA e a primeira resposta humana (aparelho, CRM ou ligação atendida) no mesmo contato, em até 24h.",
    },
    {
      label: "Taxa de handoff",
      ia: true,
      value: data ? fmtPct(data.handoff.contacts, data.handoff.ia_contacts) : "—",
      sub: data ? `${fmtNumber(data.handoff.contacts)} de ${fmtNumber(data.handoff.ia_contacts)} leads com IA` : undefined,
      tooltip: "% dos leads atendidos pela IA que tiveram intervenção humana no período.",
    },
    {
      label: "Falha de envio",
      value: data ? fmtPct(data.send.failed, data.send.api_total) : "—",
      sub: data ? `${fmtNumber(data.send.failed)} de ${fmtNumber(data.send.api_total)} envios via API · clique p/ ver logs` : undefined,
      color: data && data.send.api_total > 0 && data.send.failed / data.send.api_total > 0.02 ? "var(--danger)" : undefined,
      tooltip: "Clique para abrir os envios falhados do período.",
      onClick: () => setShowFailures(true),
    },
    {
      label: "Taxa de entrega",
      // Denominador = o que realmente saiu (API ok + aparelho). status_sent
      // não serve: a Meta não o emite pra mensagem do aparelho/coex, que
      // entra direto como delivered — dava taxa >100%.
      value: data ? fmtPct(data.delivery.delivered, data.send.api_total - data.send.failed + data.messages.by_source.aparelho) : "—",
      sub: data ? `${fmtNumber(data.delivery.delivered)} entregues de ${fmtNumber(data.send.api_total - data.send.failed + data.messages.by_source.aparelho)} enviadas` : undefined,
      tooltip: "Statuses da Meta: delivered ÷ mensagens que saíram (envios via API bem-sucedidos + aparelho). Envios de Instagram/Messenger não geram status de entrega e podem puxar a taxa um pouco pra baixo.",
    },
    {
      label: "Taxa de leitura",
      value: data ? fmtPct(data.delivery.read, data.delivery.delivered) : "—",
      sub: data ? `${fmtNumber(data.delivery.read)} lidas de ${fmtNumber(data.delivery.delivered)} entregues` : undefined,
      tooltip: "Statuses da Meta: read ÷ delivered.",
    },
    {
      label: "Leads novos",
      value: data ? fmtNumber(data.leads.new) : "—",
      sub: data ? `${fmtNumber(data.leads.active)} ativos no período` : undefined,
    },
    {
      label: "Leads atendidos pela IA",
      ia: true,
      value: data ? fmtNumber(data.ia_per_lead.leads) : "—",
      sub: data ? `${fmtPct(data.ia_per_lead.leads, data.leads.active)} dos ${fmtNumber(data.leads.active)} leads ativos` : undefined,
      tooltip: "Leads distintos que receberam resposta da IA no período. A % compara com os leads ativos (qualquer interação no período) — inclui os antigos da base que a IA reengajou.",
    },
    {
      label: "Clientes curiosos",
      value: data ? fmtNumber(data.leads.curious) : "—",
      sub: data ? `${fmtPct(data.leads.curious, data.leads.engaged)} dos ${fmtNumber(data.leads.engaged)} leads que chamaram` : undefined,
      tooltip: "Leads que enviaram no máximo 2 mensagens no período: chamaram e não seguiram a conversa.",
    },
    {
      label: "Curiosos atendidos pela IA",
      ia: true,
      value: data ? fmtNumber(data.leads.curious_ia) : "—",
      sub: data ? `${fmtPct(data.leads.curious_ia, data.ia_per_lead.leads)} dos ${fmtNumber(data.ia_per_lead.leads)} atendidos pela IA` : undefined,
      tooltip: "Leads que a IA respondeu mas que enviaram no máximo 2 mensagens no período — receberam atendimento e não seguiram a conversa.",
    },
    {
      label: "Leads de anúncio (CTWA)",
      value: data ? fmtNumber(data.leads.ctwa) : "—",
      sub: data ? `${fmtPct(data.leads.ctwa, data.leads.new)} dos leads novos` : undefined,
      tooltip: "Leads distintos com clique de anúncio (click-to-WhatsApp) registrado no período.",
    },
    {
      label: "Chamadas",
      value: data ? fmtNumber(data.calls.total) : "—",
      sub: data ? `${fmtNumber(data.calls.answered)} atendidas · média ${fmtDuration(data.calls.avg_duration_seconds)}` : undefined,
    },
  ];

  const bySource = data?.messages.by_source;
  const sourceTotal = bySource ? SOURCE_META.reduce((s, m) => s + bySource[m.key], 0) : 0;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
      {/* Filtros: período + agente + conexão */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 24 }}>
        {PERIODS.map(p => {
          const active = !rangeActive && p.value === period;
          return (
            <button
              key={p.value}
              onClick={() => { setPeriod(p.value); setStartDate(""); setEndDate(""); }}
              style={{
                padding: "5px 14px", fontSize: 12, borderRadius: 99,
                border: `1px solid ${active ? "var(--brand)" : "var(--line-strong)"}`,
                background: active ? "rgba(195,32,32,0.12)" : "transparent",
                color: active ? "var(--brand)" : "var(--ink-muted)",
                cursor: "pointer", transition: "all 0.12s",
              }}
            >
              {p.label}
            </button>
          );
        })}
        <div style={{ width: 1, height: 20, background: "var(--line-strong)", margin: "0 4px" }} />
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>De</span>
        <input
          type="date"
          value={startDate}
          max={endDate || today}
          onChange={e => setStartDate(e.target.value)}
          style={{ background: "var(--surface-hover)", border: `1px solid ${rangeActive ? "var(--brand)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, color: "var(--ink)" }}
        />
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>até</span>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          max={today}
          onChange={e => setEndDate(e.target.value)}
          style={{ background: "var(--surface-hover)", border: `1px solid ${rangeActive ? "var(--brand)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, color: "var(--ink)" }}
        />
        {(startDate || endDate) && (
          <button
            onClick={() => { setStartDate(""); setEndDate(""); }}
            style={{ fontSize: 11, color: "var(--ink-muted)", padding: "3px 6px", borderRadius: 4, background: "none", border: "none", cursor: "pointer" }}
          >
            Limpar
          </button>
        )}
      </div>

      {/* Agente + conexão (com busca) e escopo Todas/Minhas */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "-12px 0 24px" }}>
        <SearchSelect
          value={connectionId}
          onChange={setConnectionId}
          options={connOptions}
          placeholder="Todas as conexões"
        />
        {canSeeAll && (
          <div style={{ display: "flex", border: "1px solid var(--line-strong)", borderRadius: 99, overflow: "hidden", marginLeft: 4 }}>
            {([[false, "Todas"], [true, "Minhas"]] as const).map(([mine, label]) => {
              const active = scopeMine === mine;
              return (
                <button
                  key={label}
                  onClick={() => { setScopeMine(mine); setConnectionId(""); }}
                  title={mine ? "Só conexões de que sou dono ou participante" : "Todas as conexões da WABA"}
                  style={{
                    padding: "5px 14px", fontSize: 12, border: "none", cursor: "pointer",
                    background: active ? "rgba(195,32,32,0.12)" : "transparent",
                    color: active ? "var(--brand)" : "var(--ink-muted)",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 13, color: "var(--danger)", marginBottom: 16 }}>
          Erro ao carregar métricas. Verifique se o serviço WABA está online.
        </div>
      )}

      {/* WABA — visão geral */}
      {isLoading ? (
        <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando...</div>
      ) : (
        <>
          <SectionHeading>WABA — visão geral</SectionHeading>
          <CardGrid list={cards.filter(c => !c.ia)} />
        </>
      )}

      {!isLoading && data && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Enviadas por origem: barra empilhada + legenda */}
          <SectionCard title="Enviadas por origem">
            {sourceTotal === 0 ? (
              <EmptyChart />
            ) : (
              <div>
                <div style={{ display: "flex", width: "100%", height: 22, borderRadius: 6, overflow: "hidden", gap: 1, marginBottom: 14 }}>
                  {SOURCE_META.map(s => {
                    const v = bySource![s.key];
                    if (!v) return null;
                    return (
                      <div
                        key={s.key}
                        title={`${s.label}: ${fmtNumber(v)}`}
                        style={{ width: `${(v / sourceTotal) * 100}%`, minWidth: 2, background: s.color }}
                      />
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                  {SOURCE_META.map(s => (
                    <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color }} />
                      <span style={{ fontSize: 13, color: "var(--ink)" }}>{s.label}</span>
                      <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>
                        {fmtNumber(bySource![s.key])} ({fmtPct(bySource![s.key], sourceTotal)})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Mensagens por dia">
            {data.daily.length === 0 ? (
              <EmptyChart />
            ) : (
              <Suspense fallback={<ChartFallback />}>
                <WabaMetricsChart type="daily" data={data.daily} />
              </Suspense>
            )}
          </SectionCard>

          <SectionCard title="Enviadas por origem ao longo dos dias">
            {data.daily.length === 0 ? (
              <EmptyChart />
            ) : (
              <Suspense fallback={<ChartFallback />}>
                <WabaMetricsChart type="sources" data={data.daily} />
              </Suspense>
            )}
          </SectionCard>

          {data.top_connections.length > 0 && (
            <SectionCard title="Top conexões por volume">
              <Suspense fallback={<ChartFallback />}>
                <WabaMetricsChart
                  type="top"
                  data={data.top_connections.map(t => ({ name: t.name, inbound: t.inbound, outbound: t.outbound }))}
                />
              </Suspense>
            </SectionCard>
          )}
        </div>
      )}

      {/* Inteligência artificial */}
      {!isLoading && (
        <>
          <SectionHeading top>Inteligência artificial</SectionHeading>
          <CardGrid list={cards.filter(c => c.ia)} />
        </>
      )}
      {!isLoading && data && (
        <div style={{ marginTop: 16 }}>
          <SectionCard title="Mensagens enviadas pela IA por lead">
            {data.ia_funnel.every(b => b.leads === 0) ? (
              <EmptyChart />
            ) : (
              <Suspense fallback={<ChartFallback />}>
                <WabaMetricsChart type="buckets" data={data.ia_funnel} />
              </Suspense>
            )}
          </SectionCard>
        </div>
      )}

      {/* Popup de envios falhados */}
      {showFailures && (
        <div
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any, padding: 24 }}
          {...backdropClose(() => setShowFailures(false))}
        >
          <DialogPanel
            onClose={() => setShowFailures(false)}
            label="Envios falhados"
            style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, width: "min(920px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}
          >
            <div style={{ padding: "18px 22px 12px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Envios falhados no período</span>
              {failData && (
                <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                  {fmtNumber(failData.failures.length)}{failData.failures.length === 200 ? " (mostrando os 200 mais recentes)" : ""}
                </span>
              )}
            </div>
            <div style={{ overflowY: "auto", padding: "6px 22px 18px" }}>
              {failLoading && <div style={{ fontSize: 13, color: "var(--ink-muted)", padding: "14px 0" }}>Carregando...</div>}
              {failData && failData.failures.length === 0 && (
                <div style={{ fontSize: 13, color: "var(--ink-muted)", padding: "14px 0" }}>Nenhum envio falhado no período.</div>
              )}
              {failData && failData.failures.map(f => (
                <div key={f.id} style={{ borderBottom: "1px solid var(--surface-input)", padding: "11px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 88, fontSize: 12, color: "var(--ink-secondary)" }}>{fmtLogTime(f.created_at)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--ink)", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                      <span style={{ fontWeight: 600 }}>{f.connection_name}</span>
                      {f.to_number && <span style={{ color: "var(--ink-secondary)" }}>→ {f.to_number}</span>}
                      <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{f.event_type}{f.source ? ` · ${f.source}` : ""}</span>
                    </div>
                    {f.send_error && (
                      <div title={f.send_error} style={{ fontSize: 12, color: "var(--danger)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.send_error}
                      </div>
                    )}
                  </div>
                  {f.execution && (
                    <button
                      onClick={() => setExecSel({
                        exec: f.execution!,
                        stepsUrl: `/agents/${f.execution!.agent_id}/executions/${f.execution!.id}/steps`,
                        subtitle: f.agent_name ?? undefined,
                      })}
                      title={`Execução ${execMeta(f.execution.status).label} do agente ${f.agent_name ?? ""}`}
                      style={{ flexShrink: 0, fontSize: 12, color: "var(--brand)", background: "transparent", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "5px 12px", cursor: "pointer" }}
                    >
                      Ver execução
                    </button>
                  )}
                </div>
              ))}
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Detalhe da execução (API do Carmillion) */}
      {execSel && (
        <ExecModal
          exec={execSel.exec}
          stepsUrl={execSel.stepsUrl}
          subtitle={execSel.subtitle}
          onClose={() => setExecSel(null)}
        />
      )}
    </div>
  );
}
